import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import moment from 'moment';

import { usePollSyncs } from "./usePollSyncs";
import CurrentStatus from './CurrentStatus';
import { IReduxAppState } from "../../reducers";

import InstructiveButtonCard from '../../yoello-lib/modules/components/UI/Cards/InstructiveButton';
import { HTTPMethods } from '../../yoello-lib/modules/API/API';
import useTask from "../../hooks/useTask";
import { useAPIV2 } from '../../yoello-lib/modules/API2/useAPIv2';
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { IMenuData } from '../../components/manageMenu/types';
import IMenuItemBody from '../../interfaces/IMenuItemBody';
import ISubCategory from '../../components/menu/categories/ISubCategory';
import { useTranslation } from "react-i18next";

const MESSAGE_TIMEOUT = 5000; // ms

type ProgressState = 'error' | 'hidden' | 'active' | 'success'
export interface IProgressMessage {
    icon: React.ReactNode;
    title: string;
    description: string;
}
export interface IProgressMessages {
    success: IProgressMessage;
    error: IProgressMessage;
    active: IProgressMessage;
    hidden: IProgressMessage;
}
export interface ISyncCard {
    callAPI: Function;
    title: string;
    description: string | JSX.Element;
    descriptionAfter?: string | JSX.Element;
    buttonText: string;
    buttonAside?: string | JSX.Element;
    messages: IProgressMessages;
    query: (venue: string) => string;
    getStatusId: (syncStatus: any) => string;
    onSyncCompleted?: () => void;
    syncOnlyOneVenue?: string;
    hasAddItemsToMenuSection?:boolean;
    disabled?: boolean;
    isEposNow?:boolean;
}

const SyncCard = (props: ISyncCard) => {
    const venues = useSelector((state: IReduxAppState) => state.venue);
    const [statuses, setStatuses] = useState<string[]>([]);
    const [showProgress, setShowProgress] = useState<boolean>(false);
    const [progress, setProgress] = useState<ProgressState>('hidden');
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const { setSyncIds, stopPolling, successes, fails, finisheds, resetState } = usePollSyncs();
    const { increaseDoneTasks } = useTask();
    const [menusList, setMenusList] = useState<IMenuData[]>([]);
    const [orphanItemsList, setOrphanItemsList] = useState<IMenuItemBody[]>([]);
    const selectedVenueId = useSelector(
        (state: IReduxAppState) => state.selectedVenueId
      );
    const { t } = useTranslation();

  const { callAPI:manageMenuAPI, } = useAPIV2(APIExtensions.menus, {
        onMountDisabled:true,
        yoelloAPIParams: { venue_id: selectedVenueId, size: 200, include_disabled: true, },
        isPaginated: true,
    
    });
    
    const { callAPI:menuItemsAPI,apiData:menuItems} = useAPIV2(APIExtensions.menusItems, {
        onMountDisabled:true,
        yoelloAPIParams: {venue_id: selectedVenueId, size: 200, include_disabled: true, },
        isPaginated: true,
        params:{ orphaned:true,sold_as_extra_only:false,}
        
    });
    
    // eslint-disable-next-line
    const { callAPI,apiData:subcategories} = useAPIV2(APIExtensions.menuItemSubcategories, {
        yoelloAPIParams: {venue_id: selectedVenueId, size: 200, include_disabled: true,  },
        isPaginated: true,
        
    });

    useEffect(() => {if(props.hasAddItemsToMenuSection){
            getAddItemsToMenuData()
        }
    // eslint-disable-next-line
    }, [props.hasAddItemsToMenuSection])


    const getAddItemsToMenuData = ()=>{
        manageMenuAPI(HTTPMethods.GET).then(res=>{
            setMenusList(res.data as IMenuData[])
          })
      
          menuItemsAPI(HTTPMethods.GET).then(res=>setOrphanItemsList(res.data as IMenuItemBody[]))
      
    }
    


    const startSyncCall = async (callAPI: Function) => {
        const statuses = [];
        // First if should be removed once the syncing for EposNow is the same with other integrations on back-end side 
        if(props.isEposNow){
            if (props.syncOnlyOneVenue) {
                const syncStatus = await callAPI(HTTPMethods.POST, {
                    body: {
                        "requested_on": moment.utc().format('YYYY-MM-DDTHH:mm:ss')
                    },
                });
    
                statuses.push(props.getStatusId(syncStatus));
            } else {
                for (let venue in venues) {
                    const syncStatus = await callAPI(HTTPMethods.POST, {
                        body: {
                            "requested_on": moment.utc().format('YYYY-MM-DDTHH:mm:ss')
                        },
                        query: `v2/venues/${props.query(venue)}/sync_menu`
                    })
        
                    statuses.push(props.getStatusId(syncStatus));
                }
            }
        }else{
            if (props.syncOnlyOneVenue) {
                const syncStatus = await callAPI(HTTPMethods.POST, {
                    body: {
                        "requested_on": moment.utc().format('YYYY-MM-DDTHH:mm:ss'),
                        "venue_id":props.syncOnlyOneVenue
                    },
                });
    
        statuses.push(syncStatus?.data?.id);
            } else {
                for (let venue in venues) {
                    const syncStatus = await callAPI(HTTPMethods.POST, {
                        body: {
                            "requested_on": moment.utc().format('YYYY-MM-DDTHH:mm:ss'),
                            "venue_id": props.query(venue)
                    },
                    })
        statuses.push(syncStatus?.data?.id);
                }
            }
        }
        

        return statuses;
    }

    const onSync = async () => {
        resetState();
        setIsDisabled(true);
        setShowProgress(true);
        setProgress('active');
        const statuses = await startSyncCall(props.callAPI) as string[];
       setStatuses(statuses);
    }

    useEffect(() => {
        setSyncIds(statuses);
        //eslint-disable-next-line
    }, [statuses])

    useEffect(() => {

        if (statuses.length === finisheds.length) {
            stopPolling();
            setIsDisabled(false);
            increaseDoneTasks();
            if(props.onSyncCompleted){
                props.onSyncCompleted();
            }
        getAddItemsToMenuData()
    }
        //eslint-disable-next-line
    }, [finisheds, statuses])

    useEffect(() => {
        setProgress(
            fails.length > 0 ? 'error' :
                successes.length > 0 && successes.length === statuses.length ? 'success' :
                    'active'
        )
    }, [successes, fails, statuses])

    const renderCurrentStatus = () => {
        if (!showProgress) return null;

        const currentStatus = <CurrentStatus
            isActive={showProgress}
            isDeterminate={false}
            percentComplete={fails.length > 0 ? 100 : (finisheds.length / statuses.length) * 100}
            progress={progress}
            icon={props.messages[progress].icon}
            title={props.messages[progress].title}
            description={props.messages[progress].description}
        />

        return progress === 'active'
            ? currentStatus
            : <ComponentHideout Component={currentStatus} duration={MESSAGE_TIMEOUT} />
    }

    return (
        <InstructiveButtonCard
            title={props.title}
            description={props.description}
            descriptionAfter={props.descriptionAfter}
            buttonText={props.buttonText}
            buttonAside={props.buttonAside}
            disabled={isDisabled || !!props.disabled}
            onClick={onSync}
            color="primary"
            status={renderCurrentStatus()}
            isEposNow={props.isEposNow}
            AddToMenuControllerProps={venues[selectedVenueId].venueSettings?.on_menu_setup && props.hasAddItemsToMenuSection && props.isEposNow ? {
                menuItemsNotInMenu:orphanItemsList,
                orphanedMenuItemsAPI:menuItemsAPI,
                manageMenuAPI:manageMenuAPI,
                menuItems:menuItems as IMenuItemBody[],
                subcategories:subcategories as ISubCategory[], 
                venueId:selectedVenueId,
                menusList:menusList,
                addBoxExtraInfo:t("manageMenu.form.ItemsNeedToBeAdded"),
                updateData:(updatedItems:IMenuItemBody[],updatedMenus?:IMenuData[]) => {
                    setOrphanItemsList(updatedItems)
                    if(updatedMenus){
                        setMenusList(updatedMenus)
                    }
                
                }
              } : undefined}
        />
    )
}

export default SyncCard;

interface ComponentHideoutProps {
    Component: JSX.Element;
    duration: number;
}

const ComponentHideout = (props: ComponentHideoutProps) => {
    const [visibility, setVisibility] = useState(true);

    setTimeout(() => {
        setVisibility(false)
    }, props.duration);

    return visibility ? props.Component : null;
}
